import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  faPhone,
  faGlobe,
  faMapMarkerAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useParams, Link } from "react-router-dom";
const ViewCompanyPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [phones, setPhones] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [extraInfo, setExtraInfo] = useState("");
  const [bgPreview, setBgPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [gallery, setGallery] = useState([]);

  // Модальное окно
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(`/api/get_user.php?id=${id}`);
        const data = await res.json();
  
        if (data.success && data.user) {
          // 1. Сохраняем данные пользователя (компании)
          const companyData = data.user;
          setCompany(companyData);
  
          // 2. Берем массивы прямо из КОРНЯ ответа (data), а не из companyData!
          if (Array.isArray(data.phones))
            setPhones(data.phones.map((p) => p.phone));
          
          if (Array.isArray(data.websites))
            setWebsites(data.websites.map((w) => w.url));
          
          if (Array.isArray(data.addresses))
            setAddresses(data.addresses.map((a) => a.address));
          
          if (Array.isArray(data.gallery))
            setGallery(data.gallery.map((g) => `/api/uploads/${g.image}`));
  
          // 3. extraInfo (если PHP возвращает его в корне ответа data)
          if (data.extraInfo) {
            setExtraInfo(data.extraInfo);
          } else if (companyData.extraInfo) {
            // На случай, если он остался внутри user
            setExtraInfo(companyData.extraInfo);
          }
  
          // 4. Картинки (лого и фон) берем из объекта пользователя
          if (companyData.companyLogo)
            setLogoPreview(`/api/uploads/${companyData.companyLogo}`);
          
          if (companyData.background)
            setBgPreview(`/api/uploads/${companyData.background}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchCompany();
  }, [id]);

  if (!company) return <p className="text-center mt-10"> {t("loading")}...</p>;

  const openModal = (index) => {
    setModalIndex(index);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  return (
    <section className="max-w-7xl opacity-0 animate-[show_1s_forwards] h-max m-auto bg-white min-h-screen">
      {/* Фон */}
      <div className="relative w-full h-[230px] max-md:h-[120px] border-b-4 border-[#006674]">
        {bgPreview ? (
          <img
            src={bgPreview}
            alt="background"
            className="size-full object-cover"
          />
        ) : (
          <div className="size-full bg-gray-400 flex justify-center items-center text-3xl text-white">
            No Background
          </div>
        )}

        {/* Логотип */}
        <div className="absolute top-44 max-md:top-20 left-6">
          <div className="w-[160px] h-[160px] max-md:w-[130px] max-md:h-[130px] rounded-xl shadow-2xl bg-white flex items-center justify-center">
            {logoPreview ? (
              <img
                src={logoPreview}
                className="w-full h-full object-contain rounded-xl"
                alt="company logo"
              />
            ) : (
              <span className="text-gray-500">No Logo</span>
            )}
          </div>
        </div>
      </div>

      <h1 className="text-4xl max-lg:text-4xl max-md:text-2xl font-normal text-[#006674] pb-6 uppercase pl-52 max-md:pl-44 mt-6">
        {company.companyName}
      </h1>

      {/* <hr className="w-full mt-6 h-[1px]/border-[#006674]" /> */}

      <div className="p-6 max-md:p-3 mt-12 flex flex-col gap-6">
        {extraInfo && (
          <div>
            <h3 className="text-2xl max-sm:text-xl mb-3 text-[#006674]">
              {t("information")}
            </h3>
            <p className="max-w-2xl text-gray-700 whitespace-pre-line">
              {extraInfo}
            </p>
          </div>
        )}

        {phones.length > 0 && (
          <div>
            <h3 className="text-2xl max-sm:text-xl mb-3 text-[#006674]">
              {t("telephone")}
            </h3>
            <ul className="flex flex-col gap-2">
              {phones.map((phone, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-800">
                  <div className="bg-blue-500 text-white w-10 h-10 text-lg rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faPhone} className="text-white" />
                  </div>
                  <a href={`tel:${phone}`} className="hover:underline text-lg">
                    {phone}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {websites.length > 0 && (
          <div>
            <h3 className="text-2xl mb-3 max-md:text-xl text-[#006674]">
              {t("websiteurl")}
            </h3>
            <ul className="flex flex-col gap-2">
              {websites.map((site, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-800">
                  <div className="bg-blue-500 text-white w-10 h-10 text-lg rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faGlobe} className="text-white" />
                  </div>

                  <a
                    href={site.startsWith("http") ? site : `https://${site}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline text-lg"
                  >
                    {site}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {addresses.length > 0 && (
          <div>
            <h3 className="text-2xl mb-3 max-md:text-xl text-[#006674]">
              {t("locationaddres")}
            </h3>
            <ul className="flex flex-col gap-2">
              {addresses.map((addr, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 text-lg text-gray-800"
                >
                  <div className="bg-blue-500 text-white w-10 h-10 text-lg rounded-full flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="text-white"
                    />
                  </div>

                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      addr
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {addr}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
{company && (
  <Link
    to={`/ViewCompanyVacancies/${company.id}`}
    className="block w-max shadow px-4 py-2 bg-[#006674] text-white rounded-lg hover:bg-[#00505e] transition-all duration-200"
  >
    {t("companyvacancies")}
  </Link>
)}

        {/* Галерея Swiper */}
        {gallery.length > 0 && (
          <div className="gallerybox">
            <h3 className="text-2xl mb-3 max-md:text-xl text-[#006674]">
              {t("photos")}
            </h3>
            <Swiper
              modules={[Navigation]}
              navigation
              spaceBetween={20}
              slidesPerView={4}
              breakpoints={{
                320: { slidesPerView: 1 },
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 4 },
              }}
            >
              {gallery.map((img, i) => (
                <SwiperSlide key={i}>
                  <img
                    src={img}
                    alt={`gallery-${i}`}
                    className="w-full h-40 object-cover rounded-lg shadow cursor-pointer"
                    onClick={() => {
                      setModalIndex(i);
                      setIsModalOpen(true);
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>

      {/* Модальный просмотр с Swiper */}
      {isModalOpen && (
        <div className="fixed galleryview inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <button
            className="absolute top-4 right-4 text-white text-3xl"
            onClick={() => setIsModalOpen(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <Swiper
            initialSlide={modalIndex}
            navigation
            modules={[Navigation]}
            className="max-w-[90%] max-h-[90%]"
          >
            {gallery.map((img, i) => (
              <SwiperSlide key={i}>
                <img
                  src={img}
                  alt={`gallery-${i}`}
                  className="w-full h-full object-contain"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </section>
  );
};

export default ViewCompanyPage;
